<template>
  <div @click="selectedItem = null">
    <breadcrumbs :home="recurring ? 'Recurring sales order template' : `${pick?'PICK ':''}Sales order`">
      <template>
        <v-progress-linear striped height="6" class="mx-16" v-model="itemProgress" v-if="itemProgress != 100" style="max-width:600px"/>
      </template>
      <template v-slot:right>
        <div v-if="preferences.usePicking && preferences.lineItemPicking && !salesOrder.shipped &&
                        salesOrder.lines.every(line => line.picked >= line.quantity)"
              class="green--text text--darken-1 mr-4">
          <strong>READY TO SHIP</strong>
        </div>
        <v-menu offset-y v-if="company.permissions.includes('config.write')" :close-on-content-click="false" nudge-bottom="5" left>
          <template v-slot:activator="{ on }">
            <v-icon small v-on="on">mdi-tune-variant</v-icon>
          </template>
          <v-card width="400">
            <sales-preferences @update="preferences.defaultMessage=$event.defaultMessage;preferences.useNumber=$event.useNumber;preferences.allowDiscounts=$event.allowDiscounts;preferences.lineItemPicking=$event.lineItemPicking;preferences.usePicking=$event.usePicking"/>
          </v-card>
        </v-menu>
      </template>
    </breadcrumbs>

    <v-card flat>
      <v-form v-model="valid" ref="form" :disabled="readOnly">
        <v-container class="pt-6 px-6 purtrak-scrollable" fluid>
          <v-row dense v-if="readOnly">
            <v-col class="warning--text mb-4">
              Sales order is
              <template v-if="!salesOrder.open">closed</template>
              <template v-else-if="partiallyShipped">shipped against</template>
              and cannot be modified
            </v-col>
          </v-row>

          <v-row v-if="recurring">
            <v-col><recurring-transaction v-model="recurringTransaction"/></v-col>
          </v-row>

          <v-row>
            <v-col cols="12" sm="5" md="4">
              <v-row>
                <v-col>
                  <v-autocomplete v-model="salesOrder.customerId" outlined dense label="Customer" :rules="[selectCustomer]"
                                  :items="customers" item-text="name" item-value="id" @change="changeCustomer" no-data-text="No customers found"
                                  :hint="customerHint(customer)" persistent-hint ref="customer" :disabled="pick || partiallyInvoiced">
                    <template v-slot:prepend-item>
                      <div class="pa-2 grey lighten-3 text-body-2 font-weight-medium cursor-pointer" @click="customerCreate=true">
                        <v-icon color="primary" class="mr-2">mdi-plus-thick</v-icon>Add new customer
                      </div>
                    </template>
                    <template v-slot:item="{item}">
                      <v-list-item-content>{{item.name}}</v-list-item-content>
                      <v-list-item-action class="text-caption">{{customerHint(item)}}</v-list-item-action>
                    </template>
                  </v-autocomplete>
                </v-col>
              </v-row>
              <v-row dense>
                <v-col cols="10">
                  <v-text-field label="Email" v-model="salesOrder.email" :rules="[fieldRules.maxLength(100)]" outlined dense :counter="100"
                                hint="Separate emails with a comma" :disabled="pick || partiallyInvoiced"/>
                </v-col>
                <v-col cols="2" class="text-caption mt-3">
                  <v-menu :close-on-content-click="false" min-width="350" :nudge-bottom="20">
                    <template v-slot:activator="{ on }">
                      <a href="#" v-on="on">Cc/Bcc</a>
                    </template>
                    <v-card class="pa-4">
                      <v-text-field label="Cc" v-model="salesOrder.cc" :rules="[fieldRules.maxLength(100)]" outlined dense :counter="100"
                                     hint="Separate emails with a comma"/>
                      <v-text-field label="Bcc" v-model="salesOrder.bcc" :rules="[fieldRules.maxLength(100)]" outlined dense :counter="100"
                                     hint="Separate emails with a comma"/>
                    </v-card>
                  </v-menu>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-textarea label="Bill to address" v-model="salesOrder.billToAddress" outlined dense no-resize :rows="3" :rules="[fieldRules.maxLength(250)]" hide-details="auto" :disabled="pick || partiallyInvoiced"/>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-textarea label="Ship to address" v-model="salesOrder.shipToAddress" outlined dense no-resize :rows="3" :rules="[fieldRules.maxLength(250)]" hide-details="auto" :disabled="pick || partiallyInvoiced"/>
                </v-col>
              </v-row>
            </v-col>

            <v-col cols="12" sm="6" md="5" offset-sm="1">
              <v-row dense v-if="!recurring">
                <v-col cols="6">
                  <date-picker v-model="salesOrder.date" label="Date/time" :disabled="pick || partiallyInvoiced" @change="getExchangeRate()"/>
                </v-col>
                <v-col cols="6" v-if="preferences.useNumber">
                  <v-text-field label="Number" v-model="salesOrder.number" outlined dense :disabled="pick || partiallyInvoiced" :rules="[fieldRules.required,fieldRules.maxLength(20)]"/>
                </v-col>
              </v-row>
              <v-row dense>
                <v-col cols="6">
                  <v-text-field label="Customer PO" v-model="salesOrder.po" outlined dense :disabled="pick || partiallyInvoiced" :rules="[fieldRules.maxLength(20)]" counter="20"/>
                </v-col>
                <v-col cols="6">
                  <v-combobox label="Ship via" v-model="salesOrder.shipVia" outlined dense :disabled="pick || partiallyInvoiced" :rules="[fieldRules.maxLength(20)]" counter="20" :items="shipVias"/>
                </v-col>
              </v-row>
              <v-row dense >
                <v-col cols="6" v-if="!recurring">
                  <date-picker v-model="salesOrder.due" label="Expected" date-only allow-none :disabled="pick || partiallyInvoiced"/>
                </v-col>
                <v-col  cols="6">
                  <v-autocomplete v-model="salesOrder.termId" outlined dense label="Terms"
                                  :items="terms" item-text="name" item-value="id" :disabled="pick || partiallyInvoiced"/>
                </v-col>
              </v-row>
              <v-row dense>
                <v-col cols="6" v-if="salesReps.length > 1">
                  <v-autocomplete v-model="salesOrder.salesRepId" outlined dense label="Sales representative" :disabled="!!restrictedToSalesRep || pick || partiallyInvoiced"
                                  :items="salesReps" item-text="name" item-value="id"/>
                </v-col>
                <v-col cols="6">
                  <shipment-zones-select v-model="salesOrder.shipmentZoneId" label="Shipment zone" allow-add/>
                </v-col>
              </v-row>
              <v-row v-if="sites.length>2" dense>
                <v-col>
                  <v-autocomplete v-model="salesOrder.siteId" outlined dense label="Ship from site"
                                  :items="sites" item-text="name" item-value="id" :disabled="pick || partiallyInvoiced"/>
                </v-col>
              </v-row>
            </v-col>

            <v-col class="text-h6 text-right pr-6" v-if="$vuetify.breakpoint.mdAndUp">
              Amount {{customer && this.company.settings.homeCurrency !== customer.currency ? customer.currency : ''}} {{total | currency}}
              <div class="text-body-1" v-if="!preferences.useNumber && salesOrderNumber && !recurring">Sales order #{{salesOrderNumber}}</div>
            </v-col>

          </v-row>

          <v-row dense>
            <v-col cols="1" v-if="$vuetify.breakpoint.mobile && pickingColumn">
              <scan-code @code="scan" :products="products" :items="items"/>
            </v-col>
            <v-spacer/>
            <v-col cols="12" sm="5" lg="3">
              <v-switch label="Amounts are inclusive of tax" v-model="salesOrder.taxInclusive" dense hide-details class="mt-0" :disabled="pick || partiallyInvoiced" @change="toggleTaxInclusive()"/>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <outlined-block v-model="salesOrder.lines" :rules="[lines]" :disabled="readOnly">
                <v-data-table width="100%" calculate-widths disable-filtering disable-pagination disable-sort hide-default-footer
                              :items="salesOrder.lines" item-key="key" no-data-text="There must be at least one item"
                              :headers="headers" :expanded.sync="expanded"
                              :item-class="item => item.key === selectedItem ? ($vuetify.theme.dark ? 'grey darken-1' : 'grey lighten-4') : ''"
                              class="no-sort" ref="lineTableBody"
                              @click.native.stop=""
                              @click:row="item => {selectedItem = item.key; $nextTick(() => this.$refs.form.validate())}">
                  <template v-if="pickingColumn" v-slot:header.add>
                    <scan-code @code="scan" :products="products" :items="items"/>
                  </template>
                  <template v-slot:item.add="{index}">
                    <v-icon class="handle mt-n3" :disabled="readOnly || pick || partiallyInvoiced">mdi-drag</v-icon>
                    <v-btn class="mt-n3" icon @click.stop="deleteLine(index)" x-small :disabled="readOnly || pick || partiallyInvoiced"><v-icon>mdi-delete</v-icon></v-btn>
                  </template>
                  <template v-slot:item.item="{item}">
                    <template v-if="selectedItem === item.key">
                      <multi-level-select :menuData="types" :first.sync="item.type" :second.sync="item.id" caption @change="changeLineType(item)" outlined label="Item" :disabled="pick"/>
                      <template v-if="item.type==='Product'">
                        <v-checkbox dense v-model="item.inBondProduct" class="mt-n4" :disabled="!isProductLoaded(item) || pick || partiallyInvoiced"
                                    @change="changeProductBonded(item)">
                          <template v-slot:label><span class="text-caption">Take product from in-bond inventory</span></template>
                        </v-checkbox>
                      </template>
                      <v-text-field label="Description" dense v-model="item.description" :rules="[fieldRules.required,fieldRules.maxLength(100)]" placeholder="Description" class="text-caption" outlined :disabled="pick || partiallyInvoiced"/>
                    </template>
                    <div v-else class="pl-1">
                      <div class="text-caption" :class="{'error--text':!item.type || !item.id}">{{itemDescription(item)}}{{item.type==='Product' ? (item.inBondProduct ? ' (in-bond)' : ' (duty-paid)') : ''}}</div>
                      <div :class="{'error--text':!item.description}">{{item.description || 'Description required'}}</div>
                    </div>
                  </template>
                  <template v-slot:item.sku="{item}">
                    <template v-if="!item.items">
                      <v-text-field v-if="selectedItem === item.key" label="SKU" dense outlined v-model="item.sku" class="text-caption" :disabled="pick || partiallyInvoiced" :rules="[fieldRules.maxLength(20)]"/>
                      <div v-else class="pl-1">{{item.sku}}</div>
                    </template>
                  </template>
                  <template v-slot:item.quantity="{item}">
                    <template v-if="selectedItem === item.key">
                      <decimal-number-input label="Quantity"
                                            v-model="item.quantity" prefix="" :disabled="pick || partiallyInvoiced"
                                            :suffix="suffix(item)" :precision="precision(item)" class="text-caption" @change="changeQuantityPrice(item)"
                                            :no-minimum="item.type==='Item'"/>
                      <v-checkbox v-if="item.type==='Product'" dense v-model="item.cases" hide-details :disabled="pick || partiallyInvoiced" @change="setProductPrice(item)" class="mt-n4">
                        <template v-slot:label><span class="text-caption">cases</span></template>
                      </v-checkbox>
                    </template>
                    <div v-else class="pr-1">
                      <div :class="{'error--text':item.quantity < 0 && item.type!=='Item'}">{{item.quantity | number(precision(item))}} {{suffix(item)}}</div>
                    </div>
                  </template>
                  <template v-slot:item.picked="{item}">
                    <template v-if="selectedItem === item.key">
                      <decimal-number-input label="Picked"
                                            v-model="item.picked" prefix=""
                                            :precision="precision(item)" class="text-caption"/>
                    </template>
                    <div v-else class="pr-1">
                      <div :class="{'error--text':item.picked < 0}">{{item.picked | number(precision(item))}}</div>
                    </div>
                  </template>
                  <template v-slot:item.shippedQuantity="{item}">
                    <div class="text-caption grey--text">{{item.shippedQuantity | number(precision(item))}}</div>
                  </template>
                  <template v-slot:item.invoicedQuantity="{item}">
                    <div class="text-caption grey--text">{{item.invoicedQuantity | number(precision(item))}}</div>
                  </template>
                  <template v-slot:item.price="{item}">
                    <template v-if="!item.items">
                      <decimal-number-input v-if="selectedItem === item.key" label="Unit price" v-model="item.unitPrice" :precision="4" class="text-caption" :disabled="pick || partiallyInvoiced" @change="changeQuantityPrice(item)"/>
                      <div v-else class="pr-1" :class="{'error--text':item.unitPrice<0}">${{item.unitPrice | number(4)}}</div>
                    </template>
                  </template>
                  <template v-slot:item.amount="{item}">
                    <template v-if="!item.items">
                      <decimal-number-input v-if="selectedItem === item.key" label="Amount" v-model="item.amount" class="text-caption" @change="changeAmount(item)" :no-minimum="item.type==='Item'" :disabled="pick || partiallyInvoiced"/>
                      <div v-else class="pr-1" :class="{'error--text':item.amount<0 && item.type!=='Item'}">{{item.amount | currency}}</div>
                    </template>
                  </template>
                  <template v-slot:item.taxes="{item}">
                    <template v-if="!item.items">
                      <v-autocomplete v-if="selectedItem === item.key" dense label="Tax" outlined v-model="item.taxCodeId" :disabled="pick || partiallyInvoiced"
                                :items="taxes" item-text="name" item-value="id" class="text-caption" @change="calculateTotals(true)"/>
                      <div v-else class="pl-1">{{itemTax(item)}}</div>
                    </template>
                  </template>
                  <template v-slot:expanded-item="{headers,item}">
                    <td :colspan="headers.length" class="pa-0">
                      <v-card flat @click:stop="() => selectedItem = item.key">
                        <v-simple-table dense style="width:100%">
                          <tr v-for="bundleItem in item.items" :key="bundleItem.key" :class="selectedItem === item.key ? ($vuetify.theme.dark ? 'grey darken-1' : 'grey lighten-4') : ''">
                            <td :width="headers[0].width" :min-width="headers[0].width" class="pa-0 v-data-table__divider text-center"/>
                            <td :width="headers[1].width" :min-width="headers[1].width" class="px-1 py-2 v-data-table__divider">
                              <v-text-field v-if="selectedItem === item.key" dense v-model="bundleItem.description" :rules="[fieldRules.required,fieldRules.maxLength(100)]" label="Description" class="text-caption" outlined hide-details/>
                              <div v-else class="pl-1">{{bundleItem.description}}</div>
                            </td>
                            <td :width="headers[2].width" :min-width="headers[2].width" class="px-1 py-2 v-data-table__divider">
                              <v-text-field v-if="selectedItem === item.key" dense v-model="bundleItem.sku" label="SKU" class="text-caption" :rules="[fieldRules.maxLength(20)]" outlined hide-details/>
                              <div v-else class="pl-1">{{bundleItem.sku}}</div>
                            </td>
                            <td :width="headers[3].width" :min-width="headers[3].width" class="px-2 v-data-table__divider text-right">
                              {{item.quantity}} x {{bundleItem.quantity | number(precision(bundleItem))}} {{suffix(bundleItem)}}
                            </td>
                            <td v-if="pickingColumn" :width="headers[4].width" :min-width="headers[4].width" class="px-1 py-2 v-data-table__divider"/>
                            <td :width="headers[4+(pickingColumn?1:0)].width" :min-width="headers[4+(pickingColumn?1:0)].width" class="px-1 py-2 v-data-table__divider"/>
                            <td :width="headers[5+(pickingColumn?1:0)].width" :min-width="headers[5+(pickingColumn?1:0)].width" class="px-1 py-2 v-data-table__divider"/>
                            <td :width="headers[6+(pickingColumn?1:0)].width" :min-width="headers[6+(pickingColumn?1:0)].width" class="px-1 py-2 v-data-table__divider text-right">
                              <decimal-number-input v-if="selectedItem === item.key" outlined label="Unit price" v-model="bundleItem.unitPrice" :precision="4" class="text-caption" @change="changeQuantityPrice(bundleItem, item)" hide-details/>
                              <div v-else class="pl-1">{{bundleItem.unitPrice | currency}}</div>
                            </td>
                            <td :width="headers[7+(pickingColumn?1:0)].width" :min-width="headers[7+(pickingColumn?1:0)].width" class="px-1 py-2 v-data-table__divider text-right">
                              <decimal-number-input v-if="selectedItem === item.key" outlined label="Amount" v-model="bundleItem.amount" class="text-caption" @change="changeAmount(bundleItem)" :no-minimum="bundleItem.type==='Item'" hide-details/>
                              <div v-else class="pl-1">{{bundleItem.amount | currency}}</div>
                            </td>
                            <td :width="headers[8+(pickingColumn?1:0)].width" :min-width="headers[8+(pickingColumn?1:0)].width" class="px-1 v-data-table__divider">
                              <v-autocomplete v-if="selectedItem === item.key" dense label="Tax" v-model="bundleItem.taxCodeId" :rules="[fieldRules.required]" outlined hide-details
                                        :items="taxes" item-text="name" item-value="id" class="text-caption" @change="calculateTotals(true)"/>
                              <div v-else class="pl-1">{{itemTax(bundleItem)}}</div>
                            </td>
                          </tr>
                        </v-simple-table>
                      </v-card>
                    </td>
                  </template>
                  <template v-slot:footer="{widths}">
                    <v-divider/>
                    <div @click="selectedItem = null" style="width:100%" class="py-2 px-0">
                      <div :style="'width:'+widths[0]+'px'" class="text-center">
                        <v-btn fab color="primary" x-small @click.stop="addLine" :disabled="!addOkay || readOnly || pick || partiallyInvoiced"><v-icon small>mdi-plus</v-icon></v-btn>
                      </div>
                    </div>
                  </template>
                </v-data-table>
              </outlined-block>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col cols="12" sm="6" order="2" order-sm="1">
              <v-container class="pa-0">
                <v-row>
                  <v-col>
                    <v-textarea label="Message to customer" v-model="salesOrder.memo" outlined dense rows="2" :disabled="pick" :rules="[fieldRules.maxLength(1000)]" hide-details="auto"/>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <v-textarea label="Private note" rows="2"
                                  v-model="salesOrder.privateNote" outlined dense :disabled="pick" :rules="[fieldRules.maxLength(1000)]" hide-details="auto"/>
                  </v-col>
                </v-row>
                <v-row v-if="customer && company.settings.homeCurrency !== customer.currency">
                  <v-spacer/>
                  <v-col cols="6" sm="4" md="3">
                    <decimal-number-input class="text-caption" :label="`1 ${customer.currency} =`" outlined v-model="salesOrder.exchangeRate" prefix="" :suffix="` ${company.settings.homeCurrency}`" :precision="6" hide-details :disabled="pick"/>
                  </v-col>
                </v-row>
              </v-container>
            </v-col>
            <v-col cols="12" sm="6" order="1" order-sm="2">
              <v-container class="pa-0">
                <v-row dense>
                  <v-col cols="8" class="font-weight-medium text-right">Subtotal</v-col>
                  <v-col cols="4" class="text-right pr-6">{{subTotal | currency}}</v-col>
                </v-row>
                <v-row dense align="center" v-if="preferences.allowDiscounts || salesOrder.discount.amount">
                  <v-col cols="5" md="4" offset-md="2">
                    <v-select v-model="salesOrder.discount.percent" :items="[{text:'Discount percent',value:true},{text:'Discount value',value:false}]"
                              dense outlined hide-details class="text-caption" :disabled="pick"
                              @change="calculateTotals(true)"/>
                  </v-col>
                  <v-col cols="3" md="2">
                    <decimal-number-input v-model="salesOrder.discount.amount"
                              :prefix="salesOrder.discount.percent?'':'$'" :suffix="salesOrder.discount.percent?'%':''"
                              :max="salesOrder.discount.percent?100:undefined"
                              hide-details caption :disabled="pick"
                              @change="calculateTotals(true)"/>
                  </v-col>
                  <v-col cols="4" class="text-right pr-6">{{discountValue?'-':''}}{{discountValue | currency}}</v-col>
                </v-row>
                <template v-for="tax of salesOrder.taxLines">
                  <v-row dense v-if="tax.name!=='Exempt' && tax.name!=='Out of Scope'" :key="tax.id" align="center">
                    <v-col cols="8" class="font-weight-medium text-right">{{salesOrder.taxInclusive?'Includes ':''}}{{tax.name}} @{{tax.percent}}% on {{tax.onAmount | currency}}</v-col>
                    <v-col cols="3" offset="1" class="text-right pr-6">
                      <decimal-number-input v-model="tax.amount" hide-details no-minimum :disabled="pick"/>
                    </v-col>
                  </v-row>
                </template>
                <v-row dense>
                  <v-col cols="8" class="font-weight-medium text-right">Total {{customer && company.settings.homeCurrency !== customer.currency ? `(${customer.currency})` : ''}}</v-col>
                  <v-col cols="4" class="text-right pr-6">{{total | currency}}</v-col>
                </v-row>
                <v-row dense v-if="customer && company.settings.homeCurrency !== customer.currency">
                  <v-col cols="8" class="font-weight-medium text-right">Total ({{company.settings.homeCurrency}})</v-col>
                  <v-col cols="4" class="text-right pr-6">{{total*salesOrder.exchangeRate | currency}}</v-col>
                </v-row>
              </v-container>
            </v-col>
          </v-row>

        </v-container>
      </v-form>

      <v-divider/>
      <v-card-actions class="px-6">
        <v-switch dense hide-details label="Make recurring" class="mt-0" v-model="recurring"
                  v-if="company.allowRecurringTransactions && (!salesOrderId || !salesOrder.recurringTransaction)" :disabled="onlyRecurring"/>
        <v-spacer/>
        <v-btn :plain="!readOnly" color="primary" @click="cancel">{{readOnly?'Done':'Cancel'}}</v-btn>
        <v-btn v-if="!readOnly" color="primary" :loading="saving" @click="save()" :disabled="itemProgress!==100">Save{{recurring?' template':''}}</v-btn>
      </v-card-actions>
    </v-card>

    <confirm-leave v-model="next"/>
    <customer-quick-create v-model="customerCreate" @create="newCustomer" :sales-rep-id="restrictedToSalesRep"/>
  </div>
</template>

<style>
  .pt-table-top {vertical-align: top;padding-bottom:10px !important}
</style>

<script>
import {format} from 'date-fns'
import sortableJS from 'sortablejs';
import CustomerQuickCreate from '@/views/sales/customers/CustomerQuickCreate'
import confirmLeave from '@/mixins/confirmLeave'
import customerTypes from '@/mixins/customerTypes'
import createHierarchyList from '@/mixins/createHierarchyList'
import SalesPreferences from '@/views/sales/SalesPreferences'
import RecurringTransaction from '@/components/RecurringTransaction'
import ShipmentZonesSelect from '@/views/sales/shipmentZones/SalesShipmentZonesSelect'

export default {
  name: 'SalesSalesOrdersEdit',
  mixins: [confirmLeave,createHierarchyList,customerTypes],
  components: {CustomerQuickCreate,SalesPreferences,RecurringTransaction,ShipmentZonesSelect},
  props: {
    salesOrderId: {type:String, required:false},
    estimateId: {type:String, required:false},
    customerId: {type:String, required:false},
    copySalesOrderId: {type:String, required:false},
    onlyRecurring: {type:Boolean, required:false},
    fromRecurringTransactionId: {type:String, required:false},
    date: {type:String, required:false},
    pick: {type:Boolean, required:false}    // Pick mode - only pick count can be updated
  },
  data () {
    return {
      recurring: this.onlyRecurring,
      recurringTransaction: {},
      preferences: {},
      province: null,
      terms: [{id:'',name:''}],
      taxes: [],
      salesReps: [{id:'',name:'',email:''}],
      shipVias: [],
      customers: [],
      customerCreate:false,
      salesOrderNumber: null,

      spirits: [],
      wines: [],
      nonExcises: [],
      allBulkContainers: [],
      allTanks: [],
      items: [],
      bundles: [],
      products: [],
      customerTypes: [],
      itemCategories: [],
      productCategories: [],
      salesOrder: {customerId:'',billToAddress:'',shipToAddress:'',open:true,taxInclusive:false,picked:false,
                  date:this.date ? `${this.date} 00:00:00` : format(new Date(), 'yyyy-MM-dd HH:mm:ss'), due:this.date ?? format(new Date(), 'yyyy-MM-dd'),
                  po:'',shipVia:'',lines:[],taxLines:[],memo:'',privateNote:'',termId:'',salesRepId:'',siteId:'',exchangeRate:1,
                  discount:{percent:true,amount:0},shipmentZoneId:''},
      salesOrderInput: null,
      dirty: false,
      saving: false,
      loading: false,
      itemProgress: 100,
      valid: true,
      expanded: [],
      moveOffset: 0,
      subTotal: 0,
      restrictedToSalesRep: '',
      selectedItem: null,
      lines: v => v.length>0 || 'There must be at least one line item',
      selectCustomer: v => !!v || 'Select a customer',
      itemValid: id => id !== '' || 'Please select',
      inputLine: null,
      sortable: null
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    partiallyShipped() {
      return this.salesOrder.lines.some(line => line.shippedQuantity);
    },
    partiallyInvoiced() {
      return this.salesOrder.lines.some(line => line.invoicedQuantity);
    },
    readOnly() {
      return !this.recurring && (!this.salesOrder.open || this.partiallyShipped);
    },
    sites() {
      return [{name:'',id:''},...this.$store.getters.sites(true)];
    },
    customer() {
      return this.customers.find(customer => customer.id === this.salesOrder.customerId);
    },
    bulkContainers() {
      return this.allBulkContainers.filter(bulkContainer => bulkContainer.contents.siteId === this.salesOrder.siteId);
    },
    tanks() {
      return this.allTanks.filter(tank => tank.contents.siteId === this.salesOrder.siteId);
    },
    types() {
      const types = [];
      types.push({name:'Product',id:'Product',menu:this.menuForProducts(this.products)});
      if (this.bundles.length)
        types.push({name:'Bundle',id:'Bundle',menu:this.bundles});
      if (this.items.length) {
        if (this.itemCategories.length) {
          const categories = this.createHierarchyList(this.itemCategories);
          for (const category of categories) {
            const sub = this.items.filter(item => item.category?.id === category.id);
            if (sub.length)
              category.menu = sub;
            else
              category.disabled = true;
          }
//          types.push({name:'Item',id:'Item',menu:[...categories.filter(category => !category.disabled)]});
          types.push({name:'Item',id:'Item',menu:categories});
          const leftOver = this.items.filter(item => !item.category?.id);
          if (leftOver.length)
            types[types.length-1].menu.push({divider:true},{name:'Other items',id:'x',menu:leftOver});
        }
        else
          types.push({name:'Item',id:'Item',menu:this.items});
      }
      if (this.spirits.length)
        types.push({name:'Spirit',id:'Spirit',menu:this.spirits});
      if (this.wines.length)
        types.push({name:'Wine',id:'Wine',menu:this.wines});
      if (this.nonExcises.length)
        types.push({name:'Non-Excise',id:'NonExcise',menu:this.nonExcises});
      return types;
    },
    addOkay() {
      return this.types.length;
    },
    values() {
      return this.company.permissions.includes('values');
    },
    actualSubtotal() {
      return this.salesOrder.taxInclusive
              ? this.$roundNumber(this.salesOrder.lines.reduce((total,line) =>
                    total + line.amount/ (1+(this.taxes.find(tax => tax.id === line.taxCodeId)?.salesRate ?? 0)/100), 0))
              : this.subTotal;
    },
    discountValue() {
      if (this.salesOrder.discount.percent)
        return this.$roundNumber(this.actualSubtotal * this.salesOrder.discount.amount/100);
      return this.salesOrder.discount.amount;
    },
    total() {
      return this.actualSubtotal - this.discountValue + this.salesOrder.taxLines.reduce((total,tax) => tax.amount + total, 0);
    },
    pickingColumn() {
      return this.preferences.usePicking && this.preferences.lineItemPicking;
    },
    headers() {
      const headers = [{text:'',value:'add',width:'5%',align:'center',cellClass:'pa-0',divider:true},
                      { text: 'Item/Description', value: 'item', width:'25%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true},
                      { text: 'SKU', value: 'sku' ,width:'10%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true},
                      { text: 'Quantity', value: 'quantity' ,width:'10%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true,align:'right'},
                      { text: 'Shipped', value: 'shippedQuantity' ,width:'7.5%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true,align:'right'},
                      { text: 'Invoiced', value: 'invoicedQuantity' ,width:'7.5%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true,align:'right'},
                      { text: 'Unit price', value: 'price' ,width:'10%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true,align:'right'},
                      { text: 'Amount', value: 'amount' ,width:'10%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true,align:'right'},
                      { text: 'Tax', value: 'taxes' ,width:'10%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true}];
      if (this.pickingColumn)
        headers.splice(4, 0, { text: 'Picked', value: 'picked' ,width:'7.5%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true,align:'right'});
      return headers;
    }
  },
  watch: {
    salesOrder: {
      handler() {this.dirty = true},
      immediate: true,
      deep: true
    },
    'preferences.useNumber': function() {
      if (!this.preferences.useNumber)
        delete this.salesOrder.number;
    },
    'salesOrder.salesRepId'() {
      const salesRep = this.salesReps.find(rep => rep.id === this.salesOrder.salesRepId) || '';
      if (this.preferences.defaultCcSalesRep)
        this.salesOrder.cc = salesRep.email;
      if (this.preferences.defaultBccSalesRep)
        this.salesOrder.bcc = salesRep.email;
    }
  },
  async created() {
    this.loading = true;
    if (this.salesOrderId || this.copySalesOrderId)
      this.itemProgress = 0;
    try {
      this.province = await import(`@/views/provinces/${this.company.province}/sales/shipments.js`);
    // eslint-disable-next-line no-empty
    } catch (error) {}

   const data = await Promise.all([
      this.$store.dispatch('customers', !this.salesOrderId),
      this.$store.dispatch('products'),
      this.$store.dispatch('items'),
      this.$lazyListLoad(this.productCategories, 'categories2', 'id,name,parent{id}', 100, [], [{name:'type',value:'Product'}]),
      this.$lazyListLoad(this.itemCategories, 'categories2', 'id,name,parent{id}', 100, [], [{name:'type',value:'Item'}]),
//      this.$lazyListLoad(this.products, 'products2', 'id,name,category{id}', 100, !this.salesOrderId ? [{field:'active', comparator:'eq', value:'true'}] : []),
      this.$lazyListLoad(this.bundles, 'bundles2', 'id,name,price,inBondPrice,tax{id},sellingDescription,sku,displayComponents', 100, !this.salesOrderId ? [{field:'active', comparator:'eq', value:'true'}] : []),
//      this.$lazyListLoad(this.items, 'items2', 'id,name,trackUnits,precision,category{id}', 100, [{field:'sold', comparator:'eq', value:'true'}, ...(!this.salesOrderId ? [{field:'active', comparator:'eq', value:'true'}] : [])]),
      this.$lazyListLoad(this.spirits, 'spirits2', 'id,name', 100, this.salesOrderId ? [] : [{field:'active', comparator:'eq', value:'true'}]),
      this.company.allowWine ?  this.$lazyListLoad(this.wines, 'wines2', 'id,name', 100, this.salesOrderId ? [] : [{field:'active', comparator:'eq', value:'true'}]) : [],
      this.$lazyListLoad(this.nonExcises, 'nonExcises2', 'id,name', 100, this.salesOrderId ? [] : [{field:'active', comparator:'eq', value:'true'}]),
      this.$lazyListLoad(this.salesReps, 'salesReps2', 'id,name,email,restricted,userId', 100, this.salesOrderId ? [] : [{field:'active', comparator:'eq', value:'true'}]),
      this.$lazyListLoad(this.taxes, 'taxes2', 'id,name,salesRate,groupTaxes', 100, this.salesOrderId ? [] : [{field:'active', comparator:'eq', value:'true'},{field:'salesRate', comparator:'ne', value:''}]),
      this.$lazyListLoad(this.terms, 'terms2', 'id,name', 100, this.salesOrderId ? [] : [{field:'active', comparator:'eq', value:'true'}])
    ]);
    this.customers = data[0];
    this.products = this.salesOrderId ? data[1] : data[1].filter(product => product.active);
    this.items = this.salesOrderId ? data[2].filter(item => item.sold) : data[2].filter(item => item.sold && item.active);
    const results = await this.$apiCall(`query SalesSalesOrdersEdit($edit:Boolean!, $id:ID!, $estimateId:ID!, $estimate:Boolean!) {
                                              salesOrder(id:$id) @include(if:$edit) {
                                                id,customerId,shipToAddress,billToAddress,email,cc,bcc,date,due,open,memo,privateNote,picked,shipped,
                                                number,po,termId,salesRepId,shipVia,exchangeRate,taxInclusive,siteId
                                                discount {percent, amount}
                                                lines {
                                                  type,id,cases,inBondProduct,description,sku,quantity,picked,shippedQuantity,invoicedQuantity,unitPrice,amount,taxCodeId
                                                  items {type,id,cases,inBondProduct,description,sku,quantity,shippedQuantity,invoicedQuantity,unitPrice,amount,taxCodeId}
                                                }
                                                taxLines {
                                                  id,name,amount,onAmount,percent
                                                }
                                                recurringTransaction {
                                                  name,scheduleType,advanceDays,startDate,scheduleInterval
                                                  frequency,dayOfWeek,ordinal,day,month
                                                  end,endDate,endOccurances
                                                }
                                                discount {percent, amount}
                                                shipmentZoneId
                                              }
                                              estimate(id:$estimateId) @include(if:$estimate) {
                                                customer{id},shipToAddress,billToAddress,email,cc,bcc,memo,privateNote,
                                                salesRep{id},taxInclusive
                                                discount {percent, amount}
                                                lines {
                                                  type,id,cases,inBondProduct,description,sku,quantity,amount,taxCodeId,unitPrice
                                                }
                                                taxLines {
                                                  id,name,amount,onAmount,percent
                                                }
                                              }
                                              salesPreferences {
                                                allowDiscounts
                                                useNumber
                                                defaultMessage,usePicking,lineItemPicking
                                                alwaysInBond
                                                defaultTaxInclusive
                                                defaultCc,defaultBcc,defaultCcSalesRep,defaultBccSalesRep
                                              }
                                              salesOrderNextNumber
                                              shipVias {
                                                name
                                              }
                                          }`,
                                        {edit:!!this.salesOrderId||!!this.copySalesOrderId,
                                         id:this.salesOrderId ?? this.copySalesOrderId ?? '',
                                         estimate:!!this.estimateId, estimateId:this.estimateId??''}
                                         );
    if (results.error) {
      this.$nextTick(() => this.dirty = false);
      this.$router.back();
      return;
    }

    this.restrictedToSalesRep = this.salesReps.find(rep => rep.userId === this.$store.state.user.sub && rep.restricted)?.id || '';
    this.shipVias = results.data.shipVias.map(shipVia => shipVia.name);

    if (this.salesOrderId || this.copySalesOrderId) {
      let key = Date.now();
      results.data.salesOrder.lines.forEach(item => {
        if (this.copySalesOrderId) {
          item.shippedQuantity = 0;
          item.invoicedQuantity = 0;
          item.picked = 0;
        }
        item.key = key++;
      });
      this.salesOrder = this.salesOrderId
                        ? results.data.salesOrder
                        : {...results.data.salesOrder, id:'', number:results.data.salesOrderNextNumber, date:this.salesOrder.date, due:this.salesOrder.due, po:'', open:true};
      let n = 0;
      for (const line of this.salesOrder.lines) {
        if (line.type === 'BulkContainer' || line.type === 'Tank')
          line.type = 'alcohol';
        await this.setLineDetails(line, true);

        if (this.salesOrder.taxInclusive && line.taxCodeId) {
          const tax = this.taxes.find(tax => tax.id === line.taxCodeId);
          if (this.salesOrder.taxInclusive)
            line.unitPrice += line.unitPrice * tax.salesRate/100;
          line.amount = this.$roundNumber(line.unitPrice * line.quantity);
        }
        this.itemProgress = (n++ / this.salesOrder.lines.length) * 100;
      }
      this.calculateTotals(!this.salesOrder.taxLines);
      this.edit = this.salesOrder.pendingReview || this.salesOrder.draft || this.copySalesOrderId;
      this.salesOrderNumber = this.salesOrder.number;
      this.recurring = !!this.salesOrder.recurringTransaction && !this.copySalesOrderId;
      this.recurringTransaction = this.salesOrder.recurringTransaction ?? {};
    }
    this.preferences = results.data.salesPreferences;
    if (!this.salesOrderId && !this.copySalesOrderId) {
      if (this.estimateId) {
        this.salesOrder.customerId = results.data.estimate.customer.id;
        this.salesOrder.billToAddress = results.data.estimate.billToAddress;
        this.salesOrder.shipToAddress = results.data.estimate.shipToAddress;
        this.salesOrder.email = results.data.estimate.email;
        this.salesOrder.cc = results.data.estimate.cc;
        this.salesOrder.bcc = results.data.estimate.bcc;
        this.salesOrder.salesRepId = results.data.estimate.salesRep?.id ?? '';
        this.salesOrder.taxInclusive = results.data.estimate.taxInclusive;
        this.salesOrder.memo = results.data.estimate.memo;
        this.salesOrder.privateNote = results.data.estimate.privateNote;
        this.salesOrder.discount = results.data.estimate.discount;
        this.salesOrder.taxLines = results.data.estimate.taxLines;
        let n = 0;
        for (const estimateLine of results.data.estimate.lines) {
          this.salesOrder.lines.push({...estimateLine, shippedQuantity:0, invoicedQuantity:0});
          this.itemProgress = (n++ / results.data.estimate.lines.length) * 100;
        }
      }
      else {
        this.salesOrder.salesRepId = this.restrictedToSalesRep;
        this.salesOrder.taxInclusive = this.preferences.defaultTaxInclusive;
        this.salesOrder.customerId = this.customerId;
        this.salesOrder.memo = this.preferences.defaultMessage;
        this.salesOrder.cc = this.preferences.defaultCc;
        this.salesOrder.bcc = this.preferences.defaultBcc;
        this.changeCustomer();
      }
      this.salesOrder.number = results.data.salesOrderNextNumber;
      this.salesOrder.picked = !this.preferences.usePicking;
      this.edit = true;
    }
    if (this.copySalesOrderId) {
      this.salesOrder.picked = !this.preferences.usePicking;
      this.salesOrder.date = this.date ? `${this.date} 00:00:00` : format(new Date(), 'yyyy-MM-dd HH:mm:ss');
      this.salesOrder.due = this.date ?? format(new Date(), 'yyyy-MM-dd');
    }
    this.calculateTotals(!this.salesOrder.taxLines);
    this.customerTypes = await this.buildCustomerTypes();
    this.$nextTick(() => {this.dirty = false});
    this.itemProgress = 100;
    this.loading = false;
  },

  mounted() {
    const _self = this;
    this.sortable = sortableJS.create(this.$refs.lineTableBody.$el.querySelector('tbody'), {
      handle: '.handle',
      onEnd({newIndex, oldIndex}) {
        oldIndex -= _self.moveOffset;
        const lineSelected = _self.salesOrder.lines.splice(oldIndex, 1)[0];
        _self.salesOrder.lines.splice(newIndex, 0, lineSelected);
        _self.buildExpandedList();
      },
      onStart({oldIndex}) {
        let offset = 0;
        for (let index = 0; index < oldIndex-offset; index++) {
          const line = _self.salesOrder.lines[index];
          if (_self.expanded.some(e => e.key === line.key))
            offset += 1;
        }
        _self.moveOffset = offset;
        _self.expanded.splice(0, _self.expanded.length);
      }
    })

    if (!this.salesOrderId)
      this.$refs.form.validate();
    setTimeout(() => this.$refs.customer.focus(), 1000);
  },
  beforeDestroy() {
    this.sortable.destroy();
  },

  methods: {
    async scan({type, id, quantity, cases}) {
      if (quantity === 0)
        quantity = 1;
      const existingLine = this.salesOrder.lines.find(line => line.type === type && line.id === id);
      if (existingLine) {
        if (existingLine.cases === cases)
          existingLine.picked += quantity;
        else if (!existingLine.cases && cases) {
          const product = this.products.find(product => product.id === existingLine.id);
          existingLine.picked += quantity * product.caseSize;
        }
        return;
      }
      else if (this.pick)
        return;
      const line = {key:Date.now(),type,id,cases,inBondProduct:this.preferences.alwaysInBond,description:'',errorMessage:null,amount:0,unitPrice:0,quantity,picked:quantity,shippedQuantity:0,invoicedQuantity:0,taxCodeId:null};
      this.salesOrder.lines.push(line);
      this.setLineDetails(line, false);
      this.selectedItem = line.key;
      this.$nextTick(() => this.$refs.form.validate());
    },

    itemDescription(item) {
      const type = this.types.find(type => type.id === item.type);
      if (!type)
        return 'MISSING';

      const lists = {'Product':[this.products], 'Item': [this.items], 'Bundle': [this.bundles], 'Spirit':[this.spirits], 'Wine':[this.wines], 'NonExcise': [this.nonExcises]};
      let name;
      for (const list of lists[type.id]) {
        name = list.find(i => i.id === item.id)
        if (name)
          break;
      }

      return `${type.name}: ${name?.name}`;
    },
    itemTax(item) {
      return this.taxes.find(tax => tax.id === item.taxCodeId)?.name;
    },
    menuForProducts(products) {
      if (this.productCategories.length) {
        const categories = this.createHierarchyList([...this.productCategories]);
        for (const category of categories) {
          const sub = products.filter(product => product.category?.id === category.id);
          if (sub.length)
            category.menu = sub;
          else
            category.disabled = true;
        }
//        const p = [...categories.filter(category => !category.disabled)];
        const p = categories;
        const leftOver = products.filter(product => !product.category?.id);
        if (leftOver.length)
          p.push({divider:true},{name:'Other products',id:'x',menu:leftOver});
        return p;
      }
      else
        return products;
    },
    calculateTotals(calculateTaxes) {
      this.subTotal = this.salesOrder.lines.reduce((total,line) =>
        total + (line.items ? line.items.reduce((total,line) => total+line.amount, 0) : line.amount)
      , 0);

      if (calculateTaxes) {
        this.salesOrder.taxLines = [];
        this.salesOrder.lines.forEach(line => {
          if (line.items)    // Process bundle items
            line.items.forEach(item => this.calculateTaxesOnLine(item));
          else
            this.calculateTaxesOnLine(line);
        });
      }
    },
    calculateTaxesOnLine(line) {
      const discount = 1 - (this.subTotal ? (this.salesOrder.discount.percent ? this.salesOrder.discount.amount/100 : this.salesOrder.discount.amount / this.subTotal) : 0);
      if (line.taxCodeId) {
        const taxDefinition = this.taxes.find(t => t.id === line.taxCodeId);
        if (!taxDefinition)
            return;
        for (const taxCodeId of taxDefinition.groupTaxes ?? [line.taxCodeId]) {
          let tax = this.salesOrder.taxLines.find(tax => tax.id === taxCodeId);
          if (!tax) {
            const taxDefinitionToUse = this.taxes.find(t => t.id === taxCodeId);
            if (!taxDefinitionToUse)
                return;
            tax = {id:taxCodeId,name:taxDefinitionToUse.name,percent:taxDefinitionToUse.salesRate,amount:0,onAmount:0};
            this.salesOrder.taxLines.push(tax);
          }
          tax.onAmount += discount * (this.salesOrder.taxInclusive ? line.amount/(1+tax.percent/100) : line.amount);
          tax.amount += this.$roundNumber(discount * (this.salesOrder.taxInclusive ? line.amount - line.amount/(1+tax.percent/100) : line.amount * tax.percent / 100));
        }
      }
    },
    async getExchangeRate() {
      if (this.customer && this.customer.currency !== this.company.settings.homeCurrency) {
        const results = await this.$apiCall(`query SalesSalesOrdersEdit($code:String!,$date:Date!) {
                                                currencyRate(code:$code,date:$date)
                                              }`,
                                              {code:this.customer.currency,date:this.salesOrder.date.substring(0,10)});
        if (!results.error)
          this.salesOrder.exchangeRate = results.data.currencyRate;
      }
    },
    suffix(line) {
      if (line.type === 'Product') {
        if (line.cases)
          return 'case(s)';
        const product = this.products.find(p => p.id === line.id);
        if (product?.bottlesPerUnit > 1)
          return 'unit(s)';
        if (product?.mixedPack)
          return 'pack(s)';
        if (product?.containerType === 'Can')
          return 'can(s)';
        return 'bottle(s)';
      }
      if (line.type === 'Spirit')
        return 'LAA';
      if (line.type === 'Wine' || line.type === 'NonExcise') {
          return 'litres'
      }
      if (line.type === 'Item')
        return this.items.find(i => i.id === line.id)?.trackUnits;
      if (line.type === 'Bundle')
        return '';
      return null;
    },
    precision(line) {
      if (line.type === 'Product')
        return 0;
      if (line.type === 'Item')
        return this.items.find(i => i.id === line.id)?.precision;
      if (line.type === 'Bundle')
        return 0;
      return 3;
    },
    customerHint(customer) {
      const noType = customer?.type === 'None';
      return `${noType ? '' : `Type: ${this.customerTypes.find(type => type.value === customer?.type)?.text}`}${customer?.category?.id ? `${noType?'': ', '}Category: ${customer.category.name}` : ''}`;
    },
    async addLine() {
      const line = {key:Date.now(),type:'',id:'',cases:false,inBondProduct:this.preferences.alwaysInBond,description:'',errorMessage:null,amount:0,unitPrice:0,quantity:1,picked:0,shippedQuantity:0,invoicedQuantity:0,taxCodeId:null};
      this.salesOrder.lines.push(line);
      this.setLineDetails(line, false);
      this.selectedItem = line.key;
      this.$nextTick(() => this.$refs.form.validate());
    },
    deleteLine(index) {
      this.salesOrder.lines.splice(index, 1);
      this.buildExpandedList();
      this.calculateTotals(true);
    },
    buildExpandedList() {
      this.expanded.splice(0, this.expanded.length);
      for (const line of this.salesOrder.lines) {
        if (line.type === 'Bundle') {
          const bundle = this.bundles.find(bundle => bundle.id === line.id);
          if (bundle.displayComponents)
            this.expanded.push(line);
        }
      }
    },

    async loadProduct(line) {
      const product = this.products.find(product => product.id === line.id);
      if (product.loaded)
        return;

      this.loading = true;
      const results = await this.$apiCall(`query SalesSalesOrdersEdit($id:ID!) {
                                            product(id:$id) {
                                              sku,bottleSize,caseSize,bottlesPerUnit
                                              mixedPack,containerType
                                              defaultBondedPrice,defaultBondedTax{id},sellingBondedDescription,
                                              defaultPrice,defaultTax{id},sellingDescription,
                                              bondedCustomers{customerId,tax{id},price,sku}
                                              customers{customerId,tax{id},price,sku}
                                            }
                                          }`,
                                          {id:product.id});
      this.loading = false;
      if (!results.error) {
        Object.assign(product, results.data.product);
        product.loaded = true;
      }
    },

    async loadItem(line) {
      const item = this.items.find(item => item.id === line.id);
      if (item.loaded)
        return;

      this.loading = true;
      const results = await this.$apiCall(`query SalesSalesOrdersEdit($id:ID!) {
                                            item(id:$id) {
                                              sku,defaultPrice,defaultTax{id},sellingDescription,
                                              customers{customerId,tax{id},price,sku}
                                            }
                                          }`,
                                          {id:item.id});
      this.loading = false;
      if (!results.error) {
        Object.assign(item, results.data.item);
        item.loaded = true;
      }
    },

    async loadAlcoholItem(line) {
      const item = this.getSourceItem(line);
      if (item.loaded)
        return;

      let type = line.type.toLowerCase();
      type = {nonexcise:'nonExcise',winehigh:'wine',winemedium:'wine',wineweak:'wine'}[type] ?? type;

      this.loading = true;
      const results = await this.$apiCall(`query SalesSalesOrdersEdit($id:ID!) {
                                             ${type}(id:$id) {
                                               sku,defaultPrice,defaultTax{id},sellingDescription,customers{customerId,tax{id},price,sku}
                                              }
                                            }`,
                                          {id:item.id});
      this.loading = false;
      if (!results.error) {
        Object.assign(item, results.data[type]);
        item.loaded = true;
      }
    },

    async loadBundle(line) {
      const bundle = this.bundles.find(bundle => bundle.id === line.id);
      if (bundle.loaded)
        return;

      this.loading = true;
      const results = await this.$apiCall(`query SalesSalesOrdersEdit($id:ID!) {
                                            bundle(id:$id) {
                                              items {name,type,id,quantity,price}
                                            }
                                          }`,
                                          {id:bundle.id});
      this.loading = false;
      if (!results.error) {
        Object.assign(bundle, results.data.bundle);
        bundle.loaded = true;
      }
    },

    changeLineType(line) {
      line.unitPrice = 0;
      line.taxCodeId = null;
      line.description = '';
      line.sku = '';
      this.setLineDetails(line, false);
    },

    getSourceItem(line) {
      const list = {'Item':this.items,'Spirit':this.spirits,'Wine':this.wines,'WineHigh':this.wines,'WineMedium':this.wines,'WineWeak':this.wines,'NonExcise':this.nonExcises,'Product':this.products,'Bundle':this.bundles};
      return list[line.type]?.find(item => item.id === line.id) ?? {};
    },

    async setLineDetails(line, init) {
      if (line.type !== 'Bundle')
        delete line.items;

      if (line.id === '') {
        this.$nextTick(() => this.$refs.form?.validate());
        return;
      }

      if (line.type === 'Product')
        await this.loadProduct(line);
      else if (line.type === 'Item')
        await this.loadItem(line);
      else if (line.type === 'Spirit' || line.type === 'Wine' || line.type === 'NonExcise')
        await this.loadAlcoholItem(line);
      else if (line.type === 'Bundle')
        await this.loadBundle(line);

      this.buildExpandedList();

      if (!init) {
        let customerPricing;
        if (line.type === 'Bundle') {
          const bundle = this.bundles.find(bundle => bundle.id === line.id);
          if (bundle.displayComponents) {
            this.$set(line, "items", []);
            let key = Date.now();
            for (const item of bundle.items) {
              const bundleLine = {key:key++,type:item.type,name:item.name,id:item.id,description:'',errorMessage:null,
                                  amount:0,unitPrice:item.price,quantity:item.quantity,taxCodeId:null};
              line.items.push(bundleLine);
              this.setLineDetails(bundleLine, false);
            }
          }
          else {
            delete line.items;
            line.unitPrice = !this.company.settings.lotTrackingForProducts && this.customerTypes.find(type => type.value === this.customer?.type)?.prefInBond ? bundle.inBondPrice : bundle.price;
            line.taxCodeId = bundle.tax?.id;
            this.calculateUnitPrice(line);
            line.sku = bundle.sku;
            this.changeQuantityPrice(line);
          }
          line.description = bundle.sellingDescription || bundle.name;
        }
        else if (line.type === 'Product') {
          line.inBondProduct = !!this.customer && this.customerTypes.find(type => type.value === this.customer.type)?.prefInBond || this.preferences.alwaysInBond;
          line.cases = this.customerTypes.find(type => type.value === this.customer?.type)?.prefCases ?? false;
          this.changeProductBonded(line);
        }

        else {
          const item = {Item:this.items, Spirit:this.spirits, Wine:this.wines, NonExcise:this.nonExcises}[line.type].find(item => item.id === line.id);
          customerPricing = item.customers.find(customer => customer.customerId === this.salesOrder.customerId) ??
                            item.customers.find(customer => customer.customerId === this.customer?.category?.id);
          line.unitPrice = customerPricing?.price || item.defaultPrice;
          line.taxCodeId = customerPricing?.tax.id || item.defaultTax?.id;
          this.calculateUnitPrice(line);
          line.description = line.description || item.sellingDescription || item.name;
          line.sku = customerPricing?.sku || item?.sku;
          this.changeQuantityPrice(line);
        }
      }
      this.$nextTick(() => this.$refs.form?.validate());
    },
    isProductLoaded(line) {
      return this.products.find(product => product.id === line.id)?.loaded;
    },
    changeProductBonded(line) {
      const product = this.products.find(product => product.id === line.id);
      const inBondPricing = !!this.customer && this.customerTypes.find(type => type.value === this.customer.type)?.prefInBond;
      line.description = product[inBondPricing?'sellingBondedDescription':'sellingDescription'] || product.name;
      const productCustomers = product[inBondPricing ? 'bondedCustomers' : 'customers'];
      const customerPricing = productCustomers.find(customer => customer.customerId === this.salesOrder.customerId) ??
                              productCustomers.find(customer => customer.customerId === this.customer?.category?.id);
      line.sku = customerPricing?.sku || product?.sku;
      this.setProductPrice(line);
      this.$nextTick(() => this.$refs.form.validate());
    },
    async setProductPrice(line) {
      const product = this.products.find(product => product.id === line.id);
      const inBondPricing = !!this.customer && this.customerTypes.find(type => type.value === this.customer.type)?.prefInBond;
      const productCustomers = product[inBondPricing ? 'bondedCustomers' : 'customers'];
      const customerPricing = productCustomers.find(customer => customer.customerId === this.salesOrder.customerId) ??
                              productCustomers.find(customer => customer.customerId === this.customer?.category?.id);
      line.unitPrice = customerPricing?.price || product[inBondPricing?'defaultBondedPrice':'defaultPrice'];
      line.taxCodeId = customerPricing?.tax.id || product[inBondPricing?'defaultBondedTax':'defaultTax']?.id;
      if (!customerPricing && this.province && !inBondPricing)
        await this.province.pricing(line, product, this.customer, this.taxes);
      if (inBondPricing) {
        if (!line.cases)
          line.unitPrice = line.unitPrice / product.caseSize;
      }
      else if (line.cases)
        line.unitPrice = line.unitPrice / product.bottlesPerUnit * product.caseSize;
      this.calculateUnitPrice(line);
      this.changeQuantityPrice(line);
    },
    calculateUnitPrice(line) {
      if (this.salesOrder.taxInclusive && line.taxCodeId) {
        const tax = this.taxes.find(tax => tax.id === line.taxCodeId);
        line.unitPrice += line.unitPrice * tax.salesRate/100;
        line.amount = this.$roundNumber(line.unitPrice * line.quantity);
      }
    },
    toggleTaxInclusive() {
      for (const line of this.salesOrder.lines) {
        if (line.taxCodeId) {
          const tax = this.taxes.find(tax => tax.id === line.taxCodeId);
          if (this.salesOrder.taxInclusive)
            line.unitPrice += line.unitPrice * tax.salesRate/100;
          else
            line.unitPrice = line.unitPrice / (1 + tax.salesRate/100)
          line.amount = this.$roundNumber(line.unitPrice * line.quantity);
        }
      }
      this.calculateTotals(true);
    },
    async newCustomer(customer) {
      this.customers = await this.$store.dispatch('customers', !this.salesOrderId);
      this.salesOrder.customerId = customer.id;
      this.getExchangeRate();
      this.salesOrder.salesRepId = this.customer.salesRep?.id;
      this.salesOrder.shipmentZoneId = this.customer.shipmentZone?.id;
    },
    async changeCustomer() {
      if (!this.customer) {
        this.salesOrder.email = '';
        this.salesOrder.billToAddress = '';
        this.salesOrder.shipToAddress = '';
        this.salesOrder.termId = '';
        this.salesOrder.salesRepId = '';
        this.salesOrder.shipmentZoneId = '';
        return;
      }

      if (!this.customer.loaded) {
        this.loading = true;
        const results = await this.$apiCall(`query SalesSalesOrdersEdit($id:ID!) {
                                                customer(id:$id) {
                                                  id,name,email,currency,term{id},salesRep{id},shipmentZone{id}
                                                  billingAddress{line1,line2,city,province,postalCode}
                                                  shippingAddress{line1,line2,city,province,postalCode}
                                                }
                                              }`,
                                              {id:this.customer.id});
        this.loading = false;
        if (!results.error) {
          this.customer.loaded = true;
          Object.assign(this.customer, results.data.customer);
        }
      }
      this.salesOrder.email = this.customer.email;
      const billToAddress = this.customer.billingAddress;
      this.salesOrder.billToAddress = this.customer.name + '\n' + billToAddress.line1 + '\n' + (billToAddress.line2 ? billToAddress.line2 + '\n' : '') +
                              billToAddress.city + ' ' + billToAddress.province + '  ' + billToAddress.postalCode;
      const shipToAddress = this.customer.shippingAddress;
      this.salesOrder.shipToAddress = this.customer.name + '\n' + shipToAddress.line1 + '\n' + (shipToAddress.line2 ? shipToAddress.line2 + '\n' : '') +
                              shipToAddress.city + ' ' + shipToAddress.province + '  ' + shipToAddress.postalCode;
      for (const line of this.salesOrder.lines)
        this.setLineDetails(line, false);
      this.salesOrder.termId = this.customer.term?.id;
      this.salesOrder.salesRepId = this.customer.salesRep?.id;
      this.salesOrder.shipmentZoneId = this.customer.shipmentZone?.id;
      this.getExchangeRate();
      this.calculateTotals(true);
    },
    changeQuantityPrice(line, parentLine) {
      if (line.items)
        line.items.forEach(l => this.changeQuantityPrice(l, line));
      else
        line.amount = this.$roundNumber(line.quantity * line.unitPrice * (parentLine?.quantity || 1));
      this.calculateTotals(true);
    },
    changeAmount(line) {
      line.unitPrice = line.quantity ? this.$roundNumber(line.amount / line.quantity) : 0;
      this.calculateTotals(true);
    },
    cancel() {
      this.$router.back();
    },

    isLineIncomplete(line) {
      return !line.type || !line.id || !line.description ||
              line.quantity < 0 && line.type !== 'Item' ||
              line.picked < 0 ||
              line.unitPrice < 0 ||
              line.amount < 0 && line.type !== 'Item' ||
              line.items?.length && line.items.some(subLine => this.isLineIncomplete(subLine));
    },
    async save() {
      if (!this.$refs.form.validate() || this.salesOrder.lines.some(line => this.isLineIncomplete(line))) {
        this.$store.commit('setError', {type:'warning',message:`One or more fields is missing or invalid`});
        return;
      }

      if (this.pick) {
        this.saving = true;
        const results = await this.$apiCall(`mutation SalesSalesOrdersEdit($id: ID!, $picked: [Float!]!) {
                                              salesOrderPickLines(id:$id, picked:$picked) {id,picked}
                                            }`,
                                            {id:this.salesOrder.id, picked:this.salesOrder.lines.map(line => line.picked)});
        this.saving = false;
        if (!results.error) {
          this.dirty = false;
          this.$router.back();
          this.$store.commit('setError', {type:'success',message:`${results.data.salesOrderPickLines.picked ? 'Picked':'Updated'} sales order from ${this.customer.name}`});
        }
        return;
      }

      // Build request, especially information for all items being shipped
      // eslint-disable-next-line no-unused-vars
      const {lines,open,recurringTransaction,shipped, ...input} = this.salesOrder;
      input.lines = this.salesOrder.lines.map(line => {
        let items;    // Only if this is a bundle with displayed items
        if (line.type === 'Bundle' && line.items) {
          items = line.items.map(bundleLine => (
             {type:bundleLine.type === 'Item' ? bundleLine.type : 'Product',
              cases:false,inBondProduct:false,id:bundleLine.id,sku:bundleLine.sku,description:bundleLine.description,
              quantity:bundleLine.quantity,amount:bundleLine.amount,taxCodeId:bundleLine.taxCodeId}
          ));
        }

        let amount = line.amount;
        if (input.taxInclusive && line.taxCodeId) {
          const tax = this.taxes.find(tax => tax.id === line.taxCodeId);
          amount = this.$roundNumber(line.amount / (1 + tax.salesRate/100));
        }
        return {type:line.type,cases:line.cases,inBondProduct:line.inBondProduct,id:line.id,sku:line.sku,description:line.description,
                quantity:line.quantity,picked:line.picked,amount,taxCodeId:line.taxCodeId,items};
      });
      this.salesOrderInput = input;
      this.salesOrderInput.estimateId = this.estimateId;

      // If copying or existing sales order is made recurring, create a new recurring sales order (and leave original unchanged)
      if (this.copySalesOrderId || this.recurring && !this.salesOrder.recurringTransaction)
        delete this.salesOrderInput.id;

      if (this.recurring)
        this.salesOrderInput.recurringTransaction = this.recurringTransaction;
      this.salesOrderInput.fromRecurringTransactionId = this.fromRecurringTransactionId;

      this.salesOrderInput.number = this.salesOrderInput.number ?? this.salesOrderNumber;

      this.saving = true;
      const results = await this.$apiCall(`mutation SalesSalesOrdersEdit($input: SalesOrderInput!) {
                                             salesOrderCrupdate(input:$input) {id}
                                           }`,
                                          {input:this.salesOrderInput})
      this.saving = false;
      if (!results.error) {
        this.dirty = false;
        this.$router.back();
        this.$store.commit('setError', {type:'success',message:`${this.salesOrderInput.id?'Updated':'Created'} ${this.recurring?'recurring':''} sales order ${this.recurring?'template':''} from ${this.customer.name}`});
      }
    }
  }
}
</script>